import { ComposerFilesState } from 'components/ComposerMediaContext'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import { Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { getPostData } from 'services/compose/create/createPost'
import { composerSelector } from 'services/compose/selectors'
import ComposerState, { defaultGoogleEventEndTime, defaultScheduleTimeString } from 'services/compose/state'
import { randomId } from 'services/compose/utils'
import { connectedDestinationsSelector, userDestinationsSelector } from 'services/destinations'
import { HS_EVENT_DRAFT_SAVED, trackHubspotEvent } from 'services/tracking/hubspot'
import { BRAND_GOOGLE, ContentType, sortByKeyAscending } from 'shared'
import { getState, StoreThunkAction, StoreThunkDispatch } from 'store/state'
import { V1 } from './net'

const PAGE_SIZE = 50

export function getDrafts(
  ppids: string[],
  page: number,
  query?: string,
  types?: ContentType[],
  searchInSummary?: boolean,
  pageSize: number = PAGE_SIZE
): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const postTypes = types?.length === POST_TYPES.length ? undefined : types
    const profiles = connectedDestinationsSelector(getState())

    return dispatch(V1.getDrafts(ppids, page, pageSize, query, postTypes, searchInSummary))
      .pipe(map((response: any) => {
        const drafts = response.drafts.map((data: any) => {
          const state = data.payload.state
          let recycle = state.recycle
          if (typeof recycle === 'string') {
            recycle = new Date(recycle)
          } else if (typeof recycle === 'object') {
            recycle = true
          }

          // NOTE: Backwards compatibility for drafts created before composer media refactoring
          // Replace "imageUrls" and "videoUrl" with new format
          let video = state.video
          if (!video && state.videoUrl) {
            video = {
              id: randomId(),
              url: state.videoUrl
            }
          }
          let images = Array.isArray(state.images) ? {} : state.images || {}
          const photos = [] as string[]
          if (!images && state.imageUrls && state.imageUrls.length > 0) {
            images = state.imageUrls ? state.imageUrls.reduce((acc: Record<string, any>, url: string, index: number) => {
              const id = randomId()
              acc[id] = { id, url, order: index + 1 }
              photos.push(url)
              return acc
            }, {}) : {}
          } else if (images && Object.values(images).length > 0) {
            Object.values(images).sort(sortByKeyAscending('order')).forEach((image: any) => {
              photos.push(image.url)
            })
          } else if (data.payload.photos && data.payload.photos.length > 0) {
            data.payload.photos.forEach((url: string) => {
              photos.push(url)
            })
          }

          if (Object.values(images).length === 0 && photos.length > 0) {
            images = photos.reduce((acc: Record<string, any>, url: string, index: number) => {
              const id = randomId()
              acc[id] = { id, url, order: index + 1 }
              return acc
            }, {})
          }

          const posts = Object.keys(state.posts).reduce((acc: Record<string, any>, key: string) => {
            // NOTE: Backwards compatibility for drafts created before multi comments were added to the post state
            const post = state.posts[key]
            const comments = post.comments || []
            const firstComment = post.firstComment
            if (firstComment && comments.length === 0) {
              comments.push({
                id: randomId(),
                text: typeof firstComment.text === 'string' ? firstComment.text : firstComment,
                delay: 0,
                html: firstComment.html || ''
              })
            }
            if (comments.length === 0) {
              comments.push({ id: randomId(), text: '', delay: 0, html: '' })
            }

            // Backwards compatibility for Google posts
            if (key === BRAND_GOOGLE) {
              if (!post.update) {
                post.update = {
                  buttonType: post.buttonType,
                  link: post.link || ''
                }
              }

              if (!post.offer) {
                post.offer = {
                  offer: {
                    redeemOnlineUrl: ''
                  },
                  event: {
                    title: '',
                    schedule: {
                      startDate: defaultScheduleTimeString,
                      endDate: defaultGoogleEventEndTime
                    }
                  }
                }
              }

              if (!post.event) {
                post.event = {
                  title: '',
                  schedule: {
                    startDate: defaultScheduleTimeString,
                    endDate: defaultGoogleEventEndTime
                  }
                }
              }
            }

            acc[key] = { ...post, comments }
            return acc
          }, {})

          return {
            id: data.id,
            updatedAt: data.updatedAt,
            ...data.payload,
            photos,
            featuredImageUrl: photos[0],
            state: {
              ...state,
              posts,
              recycle,
              schedule: state.schedule ? {
                ...state.schedule,
                date: new Date(state.schedule.date).toISOString(),
                time: new Date(state.schedule.time).toISOString()
              } : {
                isActive: false,
                time: new Date().toISOString(),
                date: new Date().toISOString(),
                repeat: false,
                repeatCount: 1,
                repeatPeriod: 'weeks',
                repeatUntil: 'forever',
                repeatUntilDate: new Date().toISOString()
              },
              images,
              video
            },
            profiles: data.payload.state.selectedProfiles.map((id: string) => profiles[id]).filter(Boolean)
          }
        })
        return {
          drafts,
          totalDraftsCount: response.totalCount,
          paging: {
            ...response.paging,
            page
          }
        }
      }))
  }
}

function getDraftComposerState() {
  return (_dispatch: StoreThunkDispatch, getState: getState) => {
    const state = { ...composerSelector(getState()) } as Partial<ComposerState>
    delete state.activeTab
    delete state.draft
    delete state.progress
    delete state.savedStatuses
    delete state.facebookBackgroundTemplates
    delete state.writer
    delete (state as any)._persist
    return state
  }
}

export function createDraft(files?: ComposerFilesState): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    const draft = dispatch(getPostData(undefined, undefined, files))
    const state = dispatch(getDraftComposerState())
    return dispatch(V1.createDraft({ ...draft, state, profiles: [] }))
      .pipe(tap(() => {
        dispatch(trackHubspotEvent(HS_EVENT_DRAFT_SAVED))
      }))
  }
}

export function updateDraft(id: string, files?: ComposerFilesState): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    const draft = dispatch(getPostData(undefined, undefined, files))
    const state = dispatch(getDraftComposerState())
    return dispatch(V1.updateDraft(id, { ...draft, state, profiles: [] }))
  }
}

export function deleteDrafts(draftIds: string[], ppids: string[], types: ContentType[], all?: boolean): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.deleteDrafts(draftIds, ppids, types, all))
  }
}

export function getTotalDraftsCount() {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const profiles = userDestinationsSelector(getState())
    const ppids = Object.values(profiles).map(p => p.ppid)
    return dispatch(getDrafts(ppids, 0, undefined, undefined, undefined, 1)).pipe(map((response: any) => response.totalDraftsCount))
  }
}
